// Schreiben-Assistent (#586): Versand eines Laufs – je Empfänger Text füllen, PDF erzeugen, per E-Mail und/oder
// Post verschicken, einmal ablegen, protokollieren. Die Dienste sind austauschbar (Tests mit Attrappen);
// standardDienste() verbindet sie mit graph-mail-send, epost-brief und der Akte.
import { supabase } from '../supabase/client';
import { erzeugeBlankoBriefPdf, ladeBriefpapier, ladePostSchrift } from '../dokumente/blankoBrief';
import type { PdfGestaltung } from './gestaltung';
import type { WiedervorlageWunsch } from './wiedervorlage';
import { ladeDateiBlob } from '../dokumente/aktenschrankApi';
import { speicherName } from '../storage/speicherName';
import { anschriftZuEmpfaenger, blobZuBase64 } from '../epost/anschrift';
import { legeBriefInAkte } from '../epost/ablage';
import { freigebenAnPerson } from '../dokumente/freigaben';
import { sendeBriefe, type BriefOptionen, type BriefTeil } from '../epost/epost';
import { fehlend, fuelle, mitAnlagenverzeichnis } from './platzhalter';
import { moeglich, versandSperre } from './zustellweg';
import { schlussAb } from '../dokumente/grussformel';
import { hatGrussformel } from '../../supabase/functions/_shared/grussformel';
import { base64ZuBytes, bytesZuBase64, fuegePdfZusammen } from './pdfZusammen';
import type { SchreibenAnlage, SchreibenEmpfaenger, Zustellweg } from './typen';

export type Stand = 'offen' | 'laeuft' | 'gesendet' | 'teilweise' | 'fehler';

export interface Lauf {
  schreibenId: string; betreff: string; text: string; eingaben: Record<string, string>;
  anlagen: SchreibenAnlage[]; optionen: BriefOptionen; begleittext: string;
  /** Etappe 2: gewählter Briefkopf/Absender (null = Standard bzw. ohne) – fürs Fortsetzen gespeichert */
  briefkopfId?: string | null; absenderId?: string | null;
  /** Etappe 3: Schriftform – Original per Druck/Post, E-Mail und Portal nur als Vorab-Info */
  schriftform?: boolean;
  /** Etappe 4: gewünschte Wiedervorlage und das schon angelegte Ticket (Fortsetzen legt es nach, falls es fehlt) */
  wiedervorlage?: WiedervorlageWunsch | null; wiedervorlageTicketId?: string | null;
  /** wege: noch zu bedienende Zustellwege (#587), erledigt: beim Fortsetzen schon bediente.
   *  ohneAblage/dokumentId: beim Fortsetzen schon abgelegt → nicht noch einmal */
  empfaenger: { e: SchreibenEmpfaenger; wege: Zustellweg[]; erledigt?: Zustellweg[]; zeileId: string; stand?: Stand; ohneAblage?: boolean; dokumentId?: string | null }[];
}

export interface VersandDienste {
  pdf(e: SchreibenEmpfaenger, betreff: string, text: string): Promise<string>;
  email(e: SchreibenEmpfaenger, betreff: string, begleittext: string, pdfBase64: string, anlagen: SchreibenAnlage[]): Promise<{ emailId: string | null }>;
  post(e: SchreibenEmpfaenger, pdfBase64: string, anlagen: SchreibenAnlage[], optionen: BriefOptionen, betreff: string): Promise<{ briefId: string; pdfBase64: string | null }>;
  /** Etappe 3: Druckstapel – Anschreiben + Anlagen in einem PDF (Base64) zum Ablegen und späteren Drucken */
  druck(e: SchreibenEmpfaenger, pdfBase64: string, anlagen: SchreibenAnlage[]): Promise<string>;
  /** Ablage in der Akte; dokumentId nur bei Objektakten (nötig für die Portalfreigabe) */
  ablage(e: SchreibenEmpfaenger, pdfBase64: string, betreff: string, briefId: string | null): Promise<{ dateiId: string; dokumentId: string | null } | null>;
  /** #587: abgelegtes Schreiben im Mieter- bzw. Eigentümerportal freigeben */
  portal(e: SchreibenEmpfaenger, dokumentId: string): Promise<void>;
  protokoll(zeileId: string, d: Partial<{ stand: Stand; email_id: string; epost_brief_id: string; ablage_datei_id: string; dokument_id: string; fehler: string; email_gesendet: boolean; post_gesendet: boolean; portal_gesendet: boolean; druck_gesendet: boolean; druck_datei_id: string }>): Promise<void>;
  /** Zeile für diesen Lauf sperren (Stand „laeuft“) – false, wenn sie gerade woanders verschickt wird oder schon raus ist */
  beanspruche(zeileId: string): Promise<boolean>;
}

const STANDARD_BEGLEITTEXT = '{{empfaenger.briefanrede}}\n\nanbei erhalten Sie unser Schreiben „{{betreff}}“ als PDF.\n\nMit freundlichen Grüßen\nImmobilienmanagement Schwede';
export const VORAB_HINWEIS = 'Vorab per E-Mail zur Information – das unterschriebene Original folgt per Post.';

/** Schriftform: Vorab-Hinweis nach der Anrede (erster Absatz) einfügen. */
export function mitVorabHinweis(begleit: string): string {
  const i = begleit.indexOf('\n\n');
  return i < 0 ? `${VORAB_HINWEIS}\n\n${begleit}` : `${begleit.slice(0, i)}\n\n${VORAB_HINWEIS}${begleit.slice(i)}`;
}

export async function fuehreAus(lauf: Lauf, d: VersandDienste, fortschritt?: (fertig: number, gesamt: number) => void): Promise<{ gesendet: number; teilweise: number; fehler: number }> {
  const zahl = { gesendet: 0, teilweise: 0, fehler: 0 };
  let fertig = 0;
  for (const { e, wege, erledigt = [], zeileId, stand, ohneAblage, dokumentId: vorDokument } of lauf.empfaenger) {
    if (stand === 'gesendet' || !(await d.beanspruche(zeileId))) { fertig++; fortschritt?.(fertig, lauf.empfaenger.length); continue; }
    const schreibe = async (x: Parameters<VersandDienste['protokoll']>[1]) => {
      try { await d.protokoll(zeileId, x); } catch (err) { throw new Error(`Protokoll nicht geschrieben – Versand angehalten, damit niemand doppelt etwas bekommt: ${(err as Error).message}`); }
    };
    // Sperrvermerk, Schriftform ohne Original oder fehlende Werte (z. B. Platzhalter ohne Wert beim Fortsetzen) → nichts verschicken
    const offen = fehlend(`${lauf.betreff}\n${lauf.text}`, e.werte, lauf.eingaben);
    const sperre = versandSperre(e, [...wege, ...erledigt], !!lauf.schriftform) ?? (offen.length ? `Fehlt: ${offen.join(', ')}` : null);
    if (sperre) {
      zahl.fehler++;
      await schreibe({ stand: 'fehler', fehler: sperre });
      fertig++; fortschritt?.(fertig, lauf.empfaenger.length);
      continue;
    }
    const fehler: string[] = [];
    const gut: Partial<Record<Zustellweg, boolean>> = {};
    let emailId: string | null = null, briefId: string | null = null, postPdf: string | null = null, druckPdf: string | null = null, druckDateiId: string | null = null, dateiId: string | null = null, dokumentId = vorDokument ?? null;
    try {
      const betreff = fuelle(lauf.betreff, e.werte, lauf.eingaben);
      const text = mitAnlagenverzeichnis(fuelle(lauf.text, e.werte, lauf.eingaben), lauf.anlagen);
      const pdf = await d.pdf(e, betreff, text);
      const m = moeglich(e);
      if (wege.includes('email')) {
        gut.email = false;
        if (!m.email) fehler.push('E-Mail: keine E-Mail-Adresse');
        else try {
          const begleit = fuelle((lauf.begleittext || STANDARD_BEGLEITTEXT).replace(/\{\{\s*betreff\s*\}\}/g, betreff), e.werte, lauf.eingaben);
          emailId = (lauf.schriftform
            ? await d.email(e, `Vorab: ${betreff}`, mitVorabHinweis(begleit), pdf, lauf.anlagen)
            : await d.email(e, betreff, begleit, pdf, lauf.anlagen)).emailId;
          gut.email = true;
        } catch (err) { fehler.push(`E-Mail: ${(err as Error).message}`); }
      }
      if (wege.includes('post')) {
        gut.post = false;
        if (!m.post) fehler.push('Post: keine vollständige Anschrift');
        else try {
          const r = await d.post(e, pdf, lauf.anlagen, lauf.optionen, betreff);
          briefId = r.briefId; postPdf = r.pdfBase64; gut.post = true;
        } catch (err) { fehler.push(`Post: ${(err as Error).message}`); }
      }
      const willDruck = wege.includes('druck');
      if (willDruck) {
        gut.druck = false;
        try { druckPdf = await d.druck(e, pdf, lauf.anlagen); } catch (err) { fehler.push(`Druck: ${(err as Error).message}`); }
      }
      // Einmal ablegen – bevorzugt das Post-/Druck-PDF (Anschreiben + Anlagen in einem). Fürs Portal und den Druckstapel ist die Ablage Pflicht.
      const willPortal = wege.includes('portal');
      if ((gut.email || gut.post || willPortal || druckPdf) && !ohneAblage) {
        const a = await d.ablage(e, postPdf ?? druckPdf ?? pdf, betreff, briefId).catch(() => null);
        dateiId = a?.dateiId ?? null; dokumentId = a?.dokumentId ?? null;
      }
      if (willDruck && druckPdf) {
        // Der Druckstapel liest die abgelegte Datei. Beim Fortsetzen liegt evtl. nur das Anschreiben ohne Anlagen
        // in der Akte (z. B. E-Mail ging, Druck scheiterte) → das Druck-PDF dann eigens ablegen (druck_datei_id).
        if (ohneAblage && lauf.anlagen.length) {
          const a = await d.ablage(e, druckPdf, betreff, null).catch(() => null);
          druckDateiId = a?.dateiId ?? null;
          if (druckDateiId) gut.druck = true; else fehler.push('Druck: nicht abgelegt – kann nicht in den Druckstapel');
        } else if (dateiId || ohneAblage) gut.druck = true;
        else fehler.push('Druck: nicht abgelegt – kann nicht in den Druckstapel');
      }
      if (willPortal) {
        gut.portal = false;
        if (!m.portal) fehler.push('Portal: nur für Mieter und Eigentümer mit Objekt');
        else if (!dokumentId) fehler.push('Portal: nicht in der Objektakte abgelegt');
        else try { await d.portal(e, dokumentId); gut.portal = true; } catch (err) { fehler.push(`Portal: ${(err as Error).message}`); }
      }
    } catch (err) { fehler.push((err as Error).message); }
    const teile = Object.values(gut), ok = teile.filter(Boolean).length;
    const neu: Stand = ok === 0 ? 'fehler' : ok < teile.length ? 'teilweise' : 'gesendet';
    zahl[neu === 'gesendet' ? 'gesendet' : neu]++;
    await schreibe({
      ...(gut.email !== undefined ? { email_gesendet: gut.email } : {}), ...(gut.post !== undefined ? { post_gesendet: gut.post } : {}),
      ...(gut.portal !== undefined ? { portal_gesendet: gut.portal } : {}), ...(gut.druck !== undefined ? { druck_gesendet: gut.druck } : {}),
      stand: neu, ...(emailId ? { email_id: emailId } : {}), ...(briefId ? { epost_brief_id: briefId } : {}),
      ...(dateiId ? { ablage_datei_id: dateiId } : {}), ...(druckDateiId ? { druck_datei_id: druckDateiId } : {}), ...(dokumentId ? { dokument_id: dokumentId } : {}), ...(fehler.length ? { fehler: fehler.join('; ') } : {}),
    });
    fertig++; fortschritt?.(fertig, lauf.empfaenger.length);
  }
  return zahl;
}

const escHtml = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** HTML-Signatur → Text für text_inhalt */
const signaturText = (html: string) => html
  .replace(/<br\s*\/?>/gi, '\n').replace(/<\/(p|div|li|tr|h\d)>/gi, '\n').replace(/<[^>]+>/g, '')
  .replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>')
  .split('\n').map((z) => z.trim()).filter(Boolean).join('\n');

/** Begleit-E-Mail mit der persönlichen E-Mail-Signatur des Absendenden (bereits gesäubertes HTML): die Namens-/Firmenzeilen
 *  nach dem Gruß weichen der Signatur; bringt die Signatur selbst einen Gruß mit, entfällt auch der Gruß des Textes. */
export function begleitMitSignatur(begleittext: string, signaturHtml: string | null | undefined): { text: string; html: string } {
  const absatz = (t: string) => `<p>${escHtml(t).replace(/\n/g, '<br>')}</p>`;
  if (!signaturHtml?.trim()) return { text: begleittext, html: absatz(begleittext) };
  const ab = schlussAb(begleittext);
  let koerper = begleittext.trimEnd();
  if (ab !== null) {
    const grussZeile = begleittext.slice(ab).split('\n')[0].trim();
    koerper = hatGrussformel(signaturHtml) ? begleittext.slice(0, ab).trimEnd() : `${begleittext.slice(0, ab)}${grussZeile}`;
  }
  return { text: `${koerper}\n\n${signaturText(signaturHtml)}`, html: `${absatz(koerper)}<div><br></div>${signaturHtml}` };
}

/** Echte Dienste. konto = Standardpostfach (email_konten). gestaltung = Briefkopf/Absender (ohne: eingebautes Briefpapier). */
export function standardDienste(konto: { id: string }, schreibenId: string, gestaltung?: PdfGestaltung, emailSignatur?: string | null): VersandDienste {
  let papier: Promise<string> | null = null;
  const heute = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Berlin' });
  const ladeBytes = async (a: SchreibenAnlage): Promise<Blob> => {
    if (a.drive) return ladeDateiBlob(a.drive, a.mime);
    if (a.storage) { const { data, error } = await supabase.storage.from(a.storage.bucket).download(a.storage.path); if (error || !data) throw new Error(`Anlage fehlt: ${a.name}`); return data; }
    throw new Error(`Anlage ohne Quelle: ${a.name}`);
  };
  const hochladen = async (name: string, blob: Blob): Promise<string> => {
    const path = `${konto.id}/${Date.now()}_${speicherName(name)}`;
    const { error } = await supabase.storage.from('email-anhaenge').upload(path, blob, { contentType: blob.type || 'application/pdf' });
    if (error) throw new Error(`Anhang nicht hochgeladen: ${error.message}`);
    return path;
  };
  return {
    async pdf(e, betreff, text) {
      papier ??= gestaltung ? Promise.resolve(gestaltung.hintergrund) : ladeBriefpapier();
      const doc = erzeugeBlankoBriefPdf({ empfaenger: e.anschrift, ort: gestaltung?.ort ?? 'Kirchhain', datum: heute(), betreff, text }, await papier, true,
        await ladePostSchrift(), gestaltung?.zusatz);
      return doc.output('datauristring').replace(/^data:[^,]*,/, '');
    },
    async email(e, betreff, begleittext, pdfBase64, anlagen) {
      const pdfBlob = new Blob([Uint8Array.from(atob(pdfBase64), (c) => c.charCodeAt(0))], { type: 'application/pdf' });
      const anhaenge = [{ name: `${speicherName(betreff || 'Schreiben')}.pdf`, path: await hochladen(`${betreff || 'Schreiben'}.pdf`, pdfBlob) }];
      for (const a of anlagen) anhaenge.push({ name: a.name, path: a.storage?.bucket === 'email-anhaenge' ? a.storage.path : await hochladen(a.name, await ladeBytes(a)) });
      const mail = begleitMitSignatur(begleittext, emailSignatur);
      const { data, error } = await supabase.functions.invoke('graph-mail-send', {
        body: { konto_id: konto.id, an: [e.email], betreff, html_inhalt: mail.html, text_inhalt: mail.text, anhaenge },
      });
      if (error || data?.ok === false) throw new Error(data?.error ?? error?.message ?? 'E-Mail nicht gesendet');
      return { emailId: (data?.email_id as string | null) ?? null };
    },
    async post(e, pdfBase64, anlagen, optionen, betreff) {
      const empfaenger = anschriftZuEmpfaenger(e.anschrift);
      if (!empfaenger) throw new Error('Anschrift unvollständig');
      const teile: BriefTeil[] = [{ art: 'pdf', base64: pdfBase64, name: 'Anschreiben.pdf' }];
      for (const a of anlagen) {
        const istPdf = /pdf/i.test(a.mime) || /\.pdf$/i.test(a.name);
        teile.push(a.storage ? { art: istPdf ? 'pdf' : 'bild', storage: a.storage, mime: a.mime, name: a.name }
          : { art: istPdf ? 'pdf' : 'bild', base64: await blobZuBase64(await ladeBytes(a)), mime: a.mime, name: a.name });
      }
      const typ = e.art;
      const r = await sendeBriefe([{
        empfaenger, teile, anschrift_im_dokument: true, optionen, betreff, objekt_id: e.objektId,
        bezug_typ: 'schreiben', bezug_id: schreibenId, empfaenger_typ: typ, empfaenger_id: e.id,
      }], [], undefined, true);
      const b = r.briefe[0];
      if (!b) throw new Error('Post hat keinen Brief bestätigt');
      return { briefId: b.id, pdfBase64: b.pdf_base64 ?? null };
    },
    async druck(_e, pdfBase64, anlagen) {
      if (!anlagen.length) return pdfBase64;
      const teile = [{ bytes: base64ZuBytes(pdfBase64), mime: 'application/pdf', name: 'Anschreiben' }];
      for (const a of anlagen) teile.push({ bytes: new Uint8Array(await (await ladeBytes(a)).arrayBuffer()), mime: a.mime, name: a.name });
      return bytesZuBase64(await fuegePdfZusammen(teile));
    },
    async ablage(e, pdfBase64, betreff, briefId) {
      const a = await legeBriefInAkte({ briefId, pdfBase64, betreff, objektId: e.objektId, art: e.art, empfaengerId: e.id, ordnerName: e.ordnerName, empfaengerName: e.name });
      return a.ok ? { dateiId: a.dateiId, dokumentId: a.dokumentId } : null;
    },
    async portal(e, dokumentId) {
      if (e.art !== 'mieter' && e.art !== 'eigentuemer') throw new Error('nur für Mieter und Eigentümer');
      // #607: ausdrücklich nur an diese Person – auch bei einem Serienbrief sieht jeder nur sein eigenes Schreiben
      const problem = await freigebenAnPerson(dokumentId, e.art, e.id);
      if (problem) throw new Error(problem);
    },
    async protokoll(zeileId, d) {
      const { error } = await supabase.from('schreiben_empfaenger').update({ ...d, aktualisiert_at: new Date().toISOString() }).eq('id', zeileId);
      if (error) throw new Error(error.message);
    },
    async beanspruche(zeileId) {
      // Nur offene/fehlgeschlagene Zeilen oder eine seit 10 min hängende „laeuft“-Zeile; atomar über das Update
      const alt = new Date(Date.now() - 10 * 60_000).toISOString();
      const { data, error } = await supabase.from('schreiben_empfaenger').update({ stand: 'laeuft', aktualisiert_at: new Date().toISOString() })
        .eq('id', zeileId).or(`stand.in.(offen,fehler,teilweise),and(stand.eq.laeuft,aktualisiert_at.lt.${alt})`).select('id');
      if (error) throw new Error(`Protokoll nicht geschrieben – Versand angehalten: ${error.message}`);
      return (data ?? []).length > 0;
    },
  };
}
